import { useMemo } from 'react';
import { PRICE_RANGE } from '../constants';
import { useTranslation } from '@shared/core';
import { AccommodationListingDTO, GuestRating } from '../AccommodationsConsole.types';
import { useFilterQueryParams } from './useAccommodationsConsoleSearchParamsState/useFilterQueryParams';

export const useListingFilters = (properties: AccommodationListingDTO[], isFetchingListingPrices: boolean) => {
  const { t } = useTranslation('accommodationsConsole');
  const filterPanelTrans = t('filterPanel');
  const { currentFilters, updateFilters, resetFilters } = useFilterQueryParams();

  const supportedFacilities = useMemo(
    () => [
      { label: filterPanelTrans.facilities.parking() },
      { label: filterPanelTrans.facilities.restaurant() },
      { label: filterPanelTrans.facilities.internet() },
      { label: filterPanelTrans.facilities.nonSmoking() },
      { label: filterPanelTrans.facilities.spa() },
      { label: filterPanelTrans.facilities.bar() },
      { label: filterPanelTrans.facilities.breakfast() },
      { label: filterPanelTrans.facilities.atm() },
      { label: filterPanelTrans.facilities.swimmingPool() },
      { label: filterPanelTrans.facilities.airConditioning() },
      { label: filterPanelTrans.facilities.petsAllowed() },
    ],
    [filterPanelTrans],
  );

  // Apply filters to properties
  const filteredProperties = useMemo(() => {
    let filtered = [...properties];

    // Facilities filter
    if (currentFilters.facilities?.length) {
      filtered = filtered.filter(property => {
        const propertyFacilities = property.facilities.map((f: { label: string }) => f.label);
        return currentFilters.facilities!.every(facility => propertyFacilities.includes(facility));
      });
    }

    // Star rating filter
    if (currentFilters.starRating?.length) {
      filtered = filtered.filter(property => property.starRating && currentFilters.starRating!.includes(property.starRating));
    }

    // User rating filter
    if (currentFilters.userRating?.length) {
      filtered = filtered.filter(property => {
        const rating = property.userRating?.ratingValue;
        return rating && currentFilters.userRating!.some((minRating: GuestRating) => rating >= minRating);
      });
    }

    // CUG filter
    if (currentFilters.cug) {
      filtered = filtered.filter(property => {
        const discountPercentage = property.pricingInfo?.values?.discountPercentage;
        return typeof discountPercentage === 'number' && discountPercentage > 0;
      });
    }

    // Price range filter
    if (currentFilters.priceRange && !isFetchingListingPrices) {
      const [min, max] = currentFilters.priceRange;
      // Skip filtering if at default range [MIN, MAX]
      if (min !== PRICE_RANGE.MIN || max !== PRICE_RANGE.MAX) {
        filtered = filtered.filter(property => {
          const nightlyBaseRate = property.pricingInfo?.values?.nightlyBaseRate;
          if (typeof nightlyBaseRate !== 'number') return false;
          const meetsMinPrice = nightlyBaseRate >= min;
          const meetsMaxPrice = max === PRICE_RANGE.MAX ? true : nightlyBaseRate <= max;
          return meetsMinPrice && meetsMaxPrice;
        });
      }
    }

    return filtered;
  }, [properties, currentFilters, isFetchingListingPrices]);

  // Calculate counts for each rating and facility considering other active filters
  const { facilityCounts, starRatingCounts, guestRatingCounts } = useMemo(() => {
    const counts: Record<string, number> = {};

    // For each facility, calculate how many properties would match if it was selected
    supportedFacilities.forEach(facility => {
      // Create a test filter set that includes this facility
      const testFilters = { ...currentFilters };

      // For selected facilities, show current matches with this facility
      // For unselected facilities, show matches if this facility was added
      if (!testFilters.facilities?.includes(facility.label)) {
        testFilters.facilities = [...(testFilters.facilities || []), facility.label];
      }

      // Apply all filters with this facility configuration
      let filtered = [...properties];

      // Star rating filter
      if (testFilters.starRating?.length) {
        filtered = filtered.filter(property => property.starRating && testFilters.starRating!.includes(property.starRating));
      }

      // User rating filter
      if (testFilters.userRating?.length) {
        filtered = filtered.filter(property => {
          const rating = property.userRating?.ratingValue;
          return rating && testFilters.userRating!.some((minRating: GuestRating) => rating >= minRating);
        });
      }

      // Apply facility filters
      if (testFilters.facilities?.length) {
        filtered = filtered.filter(property => {
          const propertyFacilities = property.facilities.map((f: { label: string }) => f.label);
          return testFilters.facilities!.every(f => propertyFacilities.includes(f));
        });
      }

      counts[facility.label] = filtered.length;
    });

    // Calculate star rating counts
    const starRatingCounts: Record<number, number> = {};
    [5, 4, 3, 2, 1].forEach(rating => {
      // Create a test filter set that includes this rating
      const testFilters = { ...currentFilters };
      testFilters.starRating = [rating];

      // Apply all filters except star rating
      let filtered = [...properties];

      // User rating filter
      if (testFilters.userRating?.length) {
        filtered = filtered.filter(property => {
          const rating = property.userRating?.ratingValue;
          return rating && testFilters.userRating!.some((minRating: GuestRating) => rating >= minRating);
        });
      }

      // Facility filters
      if (testFilters.facilities?.length) {
        filtered = filtered.filter(property => {
          const propertyFacilities = property.facilities.map((f: { label: string }) => f.label);
          return testFilters.facilities!.every(f => propertyFacilities.includes(f));
        });
      }

      // Apply star rating filter
      filtered = filtered.filter(property => property.starRating === rating);
      starRatingCounts[rating] = filtered.length;
    });

    // Calculate guest rating counts
    const guestRatingCounts: Record<number, number> = {};
    [9, 8, 7, 6].forEach(rating => {
      // Create a test filter set that includes this rating
      const testFilters = { ...currentFilters };
      testFilters.userRating = [rating as GuestRating];

      // Apply all filters except user rating
      let filtered = [...properties];

      // Star rating filter
      if (testFilters.starRating?.length) {
        filtered = filtered.filter(property => property.starRating && testFilters.starRating!.includes(property.starRating));
      }

      // Facility filters
      if (testFilters.facilities?.length) {
        filtered = filtered.filter(property => {
          const propertyFacilities = property.facilities.map((f: { label: string }) => f.label);
          return testFilters.facilities!.every(f => propertyFacilities.includes(f));
        });
      }

      // Apply user rating filter
      filtered = filtered.filter(property => {
        const propertyRating = property.userRating?.ratingValue;
        return propertyRating && propertyRating >= rating;
      });
      guestRatingCounts[rating] = filtered.length;
    });

    return { facilityCounts: counts as Record<string, number>, starRatingCounts, guestRatingCounts };
  }, [supportedFacilities, currentFilters, properties]);

  return {
    filters: currentFilters,
    filteredProperties,
    updateFilters,
    supportedFacilities,
    facilityCounts,
    starRatingCounts,
    guestRatingCounts,
    resetFilters,
  };
};
